/**
 * Pure helpers for the Submission Pack header (PackHeader). Kept out of the
 * component file so it exports components only (fast refresh).
 */

import { absoluteDate, absoluteTime } from '@/lib/datetime'
import { withDeadlineTime } from '@/lib/bid-deadline'
import type { PackItem } from '@/lib/api'

/**
 * "5 Jan 2026", plus the closing time when the deadline carries one.
 *
 * `hasTime` is the bid's `deadlineHasTime` (migration 183):
 * - true  → "5 Jan 2026, 2:00 pm AEDT", in the viewer's own time zone. 11:00 am
 *   AEDT is exactly UTC midnight, so the flag — not the clock — decides.
 * - false → the date alone.
 * - null / absent → a bid saved before times were recorded. Kept on the old
 *   guess, so those bids read exactly as they did: midnight means "no time
 *   given" (local or UTC — a date picker's value lands as UTC midnight, 11:00 am
 *   in Sydney), and any other time is shown, without a zone as before.
 */
export function formatDeadline(deadline: string, hasTime?: boolean | null): string {
  const date = new Date(deadline)
  if (Number.isNaN(date.getTime())) return ''
  if (hasTime === true) return withDeadlineTime(absoluteDate(deadline), { deadline, deadlineHasTime: true })
  if (hasTime === false) return absoluteDate(deadline)
  const isLocalMidnight = date.getHours() === 0 && date.getMinutes() === 0
  const isUtcMidnight = date.getUTCHours() === 0 && date.getUTCMinutes() === 0
  return isLocalMidnight || isUtcMidnight ? absoluteDate(deadline) : absoluteTime(deadline)
}

/**
 * The lodgement method most of the pack shares, or null. Shown only when at
 * least half the live items name the same method (compared case-insensitively,
 * displayed as first written) — a portal the tender only mentions for one
 * form is not "how this pack is lodged", and we never invent one.
 */
export function dominantLodgementMethod(items: PackItem[]): string | null {
  if (items.length === 0) return null
  const counts = new Map<string, { label: string; count: number }>()
  for (const item of items) {
    const label = item.method?.trim()
    if (!label) continue
    const key = label.toLowerCase()
    const entry = counts.get(key)
    if (entry) entry.count += 1
    else counts.set(key, { label, count: 1 })
  }
  let best: { label: string; count: number } | null = null
  for (const entry of counts.values()) {
    if (!best || entry.count > best.count) best = entry
  }
  return best && best.count * 2 >= items.length ? best.label : null
}
